import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { adminCreateProduct, adminUpdateProduct, adminUploadImage, resolveImage } from "@/lib/adminApi";

const EMPTY = {
  name: "", description: "", price: "", old_price: "", volume: "5L",
  color: "#0A1F44", image: "", features: [], badge: "",
};

export const ProductForm = ({ open, onClose, product, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [featureText, setFeatureText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || "",
        description: product.description || "",
        price: product.price ?? "",
        old_price: product.old_price ?? "",
        volume: product.volume || "5L",
        color: product.color || "#0A1F44",
        image: product.image || "",
        features: product.features || [],
        badge: product.badge || "",
      });
    } else {
      setForm(EMPTY);
    }
    setFeatureText("");
  }, [product, open]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      change("image", url);
      toast.success("Imagine încărcată");
    } catch (err) {
      toast.error("Eroare la încărcarea imaginii");
    } finally {
      setUploading(false);
    }
  };

  const addFeature = () => {
    const t = featureText.trim();
    if (!t) return;
    change("features", [...form.features, t]);
    setFeatureText("");
  };

  const removeFeature = (i) => change("features", form.features.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.name.trim() || form.price === "" || isNaN(Number(form.price))) {
      toast.error("Completează numele și prețul");
      return;
    }
    if (!form.image) {
      toast.error("Adaugă o imagine");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      price: Number(form.price),
      old_price: form.old_price === "" ? null : Number(form.old_price),
      volume: form.volume.trim() || "5L",
      color: form.color,
      image: form.image,
      features: form.features,
      badge: form.badge.trim() || null,
    };
    try {
      if (product) {
        await adminUpdateProduct(product.id, payload);
        toast.success("Produs actualizat");
      } else {
        await adminCreateProduct(payload);
        toast.success("Produs adăugat");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{product ? "Editează produs" : "Adaugă produs"}</DialogTitle>
          <DialogDescription className="sr-only">Formular pentru gestionarea detaliilor produsului</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image */}
          <div>
            <label className="block text-sm font-bold text-navy mb-1.5">Imagine produs</label>
            <div className="flex items-center gap-4">
              <div className="h-24 w-24 rounded-xl border-2 border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {form.image ? (
                  <img src={resolveImage(form.image)} alt="preview" className="h-full w-full object-contain" />
                ) : (
                  <Upload className="h-6 w-6 text-slate-300" />
                )}
              </div>
              <label className="cursor-pointer bg-navy hover:bg-navy-700 text-white text-sm font-bold px-4 py-2.5 rounded-xl inline-flex items-center gap-2">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Se încarcă..." : "Încarcă imagine"}
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="upload-image-input" />
              </label>
            </div>
          </div>

          <Field label="Nume">
            <input className="inp" value={form.name} onChange={(e) => change("name", e.target.value)} data-testid="pf-name" />
          </Field>
          <Field label="Descriere">
            <textarea className="inp" rows={2} value={form.description} onChange={(e) => change("description", e.target.value)} data-testid="pf-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preț (lei)">
              <input className="inp" type="number" value={form.price} onChange={(e) => change("price", e.target.value)} data-testid="pf-price" />
            </Field>
            <Field label="Preț vechi (opțional)">
              <input className="inp" type="number" value={form.old_price} onChange={(e) => change("old_price", e.target.value)} data-testid="pf-old-price" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Volum">
              <input className="inp" value={form.volume} onChange={(e) => change("volume", e.target.value)} data-testid="pf-volume" />
            </Field>
            <Field label="Badge (opțional)">
              <input className="inp" value={form.badge} onChange={(e) => change("badge", e.target.value)} placeholder="ex: NOU, PACHET" data-testid="pf-badge" />
            </Field>
          </div>
          <Field label="Culoare accent">
            <input className="h-10 w-16 rounded border" type="color" value={form.color} onChange={(e) => change("color", e.target.value)} />
          </Field>

          {/* Features */}
          <div>
            <label className="block text-sm font-bold text-navy mb-1.5">Caracteristici</label>
            <div className="flex gap-2">
              <input
                className="inp flex-1"
                value={featureText}
                onChange={(e) => setFeatureText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                placeholder="ex: Până la 400 spălări"
                data-testid="pf-feature-input"
              />
              <button onClick={addFeature} className="bg-eco text-white px-3 rounded-xl" data-testid="pf-add-feature">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <ul className="mt-2 space-y-1">
              {form.features.map((f, i) => (
                <li key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-1.5 text-sm">
                  {f}
                  <button onClick={() => removeFeature(i)}><X className="h-4 w-4 text-slate-400 hover:text-red-500" /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl font-bold text-navy hover:bg-slate-100">Anulează</button>
          <button
            onClick={submit}
            disabled={saving}
            className="bg-eco hover:bg-green-700 disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-xl inline-flex items-center gap-2"
            data-testid="pf-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {product ? "Salvează" : "Adaugă"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-bold text-navy mb-1.5">{label}</label>
    {children}
  </div>
);
